import type { GameState, GameEvent, GameEventObserver } from '@/types';
import { GameEngine } from '@core/GameEngine';

type StateListener = (state: GameState) => void;

/**
 * Central state store that wraps the GameEngine
 * and provides reactive updates to UI components
 */
export class GameStore implements GameEventObserver {
  private engine: GameEngine;
  private listeners: Set<StateListener> = new Set();
  private eventListeners: Map<GameEvent['type'], Set<(payload: unknown) => void>> = new Map();

  constructor() {
    this.engine = new GameEngine();
    this.engine.subscribe(this);
  }

  /**
   * Get current game state
   */
  getState(): Readonly<GameState> {
    return this.engine.getState();
  }

  /**
   * Get the game engine for direct actions
   */
  getEngine(): GameEngine {
    return this.engine;
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  /**
   * Subscribe to specific game events
   */
  on(eventType: GameEvent['type'], handler: (payload: unknown) => void): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    this.eventListeners.get(eventType)!.add(handler);
    return () => this.eventListeners.get(eventType)?.delete(handler);
  }

  /**
   * Handle game events from the engine
   */
  onGameEvent(event: GameEvent): void {
    // Notify specific event listeners
    const handlers = this.eventListeners.get(event.type);
    if (handlers) {
      handlers.forEach(handler => handler(event.payload));
    }

    // Notify all state listeners
    this.notifyListeners();
  }

  /**
   * Notify all state listeners of state change
   */
  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }

  /**
   * Reset the store
   */
  reset(): void {
    this.engine.reset();
    this.notifyListeners();
  }
}

// Singleton instance
let storeInstance: GameStore | null = null;

export function getStore(): GameStore {
  if (!storeInstance) {
    storeInstance = new GameStore();
  }
  return storeInstance;
}

export function resetStore(): void {
  storeInstance = null;
}
